import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { CREATE_PAYMENT_INTENT_URL } from "../../features/UrlHelper";
import "./StripePaymentModal.scss";
import { handleApiError } from "../../features/errorHandler";
import { useAuth } from "../../context/AuthContext";
import { pushGTMEvent } from "../../analytics/gtm";

const stripePromise = loadStripe(
  window.location.hostname === "triferis.sk"
    ? process.env.REACT_APP_STRIPE_PUBLIC_KEY_PROD
    : process.env.REACT_APP_STRIPE_PUBLIC_KEY
);

function formatCurrency(amount, currency) {
  switch (currency?.toUpperCase()) {
    case "EUR":
    case "€":
      return "€";
    case "USD":
    case "$":
      return "$";
    case "GBP":
    case "£":
      return "£";
    default:
      return "€";
  }
}

/**
 * 1. Formulár Stripe platby kartou
 */
function CheckoutForm({
  amount,
  onClose,
  onSuccess,
  currency = "EUR",
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });

      if (error) {
        setErrorMessage(
          error.message || "Pri spracovaní platby nastala chyba. Skontrolujte prosím údaje na karte."
        );
        setLoading(false);
      } else if (
        paymentIntent &&
        (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")
      ) {
        setLoading(false);
        onSuccess(paymentIntent.id, paymentIntent);
      } else {
        setLoading(false);
        setErrorMessage("Platba nebola dokončená. Skúste to prosím znova.");
      }
    } catch (err) {
      setLoading(false);
      setErrorMessage(err.message || "Neočakávaná chyba pri komunikácii s platobnou bránou.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-modal__form">
      <PaymentElement
        options={{
          wallets: {
            applePay: "auto",
            googlePay: "auto",
            link: "never",
          },
        }}
      />

      {errorMessage && (
        <div
          className="stripe-modal__error"
          style={{
            color: "#dc3545",
            marginTop: "15px",
            padding: "10px 14px",
            background: "#fff5f5",
            border: "1px solid #ffc9c9",
            borderRadius: "8px",
            fontSize: "0.9rem",
            lineHeight: "1.4",
          }}
        >
          {errorMessage}
        </div>
      )}

      <div
        className="stripe-modal__footer"
        style={{
          marginTop: "24px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
        }}
      >
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          disabled={loading}
        >
          Zrušiť
        </Button>
        <Button type="submit" variant="primary" disabled={loading || !stripe}>
          {loading ? (
            <span className="stripe-modal__spinner" aria-hidden="true" />
          ) : (
            `Zaplatiť ${amount ? `${amount.toFixed(2)} ${formatCurrency(currency)}` : ""}`
          )}
        </Button>
      </div>
    </form>
  );
}

/**
 * 2. Hlavný Modal komponent pre platbu kartou
 */
function StripePaymentModal({
  isOpen,
  onClose,
  orderPayload = null,
  amount: directAmount,
  productName: directProductName,
  userEmail: directUserEmail,
  currency = "EUR",
  title = "Platba kartou online",
  onSuccess: parentOnSuccess,
}) {
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [serverOrderId, setServerOrderId] = useState(null);
  const [serverOrderNumber, setServerOrderNumber] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [initError, setInitError] = useState(null);
  const [initErrorType, setInitErrorType] = useState(null);
  const { userId, userEmail: authUserEmail, firstName, lastName, userName } = useAuth();
  const requestInFlight = useRef(false);

  // Najnovšie props/auth hodnoty pre effect – effect nesmie závisieť od identity
  // orderPayload (rodič ho vytvára pri každom renderi), inak by sa intent posielal opakovane.
  const intentInputRef = useRef(null);
  intentInputRef.current = {
    orderPayload,
    directAmount,
    directProductName,
    directUserEmail,
    userId,
    authUserEmail,
    firstName,
    lastName,
    userName,
  };

  const displayAmount =
    orderPayload?.totalAmount != null
      ? parseFloat(orderPayload.totalAmount)
      : directAmount != null
        ? parseFloat(directAmount)
        : 0;

  const displayProductName =
    directProductName ||
    (orderPayload?.items?.length === 1
      ? orderPayload.items[0].productName
      : `Prihláška na krúžky (${orderPayload?.items?.length || 1} ks)`);

  useEffect(() => {
    if (!isOpen) {
      setClientSecret(null);
      setPaymentIntentId(null);
      setServerOrderId(null);
      setServerOrderNumber(null);
      setInitError(null);
      setInitErrorType(null);
      setIsSuccess(false);
      return;
    }

    if (requestInFlight.current) return;

    const {
      orderPayload,
      directAmount,
      directProductName,
      directUserEmail,
      userId,
      authUserEmail,
      firstName,
      lastName,
      userName,
    } = intentInputRef.current;

    const abortController = new AbortController();
    requestInFlight.current = true;
    setIsSuccess(false);
    setInitError(null);
    setInitErrorType(null);

    const payload = orderPayload
      ? {
        ...orderPayload,
        userId: orderPayload.userId || userId,
        paymentMethod: "card",
      }
      : {
        items: [
          {
            productName: directProductName || "Krúžok",
            unitPrice: directAmount || 0,
          },
        ],
        totalAmount: directAmount || 0,
        customerEmail: directUserEmail || authUserEmail || "",
        customerName:
          [firstName, lastName].filter(Boolean).join(" ") ||
          userName ||
          authUserEmail ||
          "Zákazník",
        userId,
        paymentMethod: "card",
      };

    axios
      .post(CREATE_PAYMENT_INTENT_URL, payload, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("authToken")}`,
        },
        signal: abortController.signal,
      })
      .then((res) => {
        if (!abortController.signal.aborted) {
          setClientSecret(res.data.clientSecret);
          setPaymentIntentId(res.data.paymentIntentId);
          setServerOrderId(res.data.orderId);
          setServerOrderNumber(res.data.orderNumber);
        }
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          const serverMsg = handleApiError(
            err,
            "Nepodarilo sa vytvoriť platobný zámer."
          );
          const errorType = err.response?.data?.type;
          setInitErrorType(errorType || null);
          setInitError(serverMsg);
        }
      })
      .finally(() => {
        requestInFlight.current = false;
      });

    return () => {
      abortController.abort();
      requestInFlight.current = false;
    };
    // Jeden PaymentIntent na jedno otvorenie modalu – payload sa zafixuje pri otvorení.
  }, [isOpen]);

  const handlePaymentSuccess = (piId, paymentIntentObj) => {
    setPaymentIntentId(piId || paymentIntentObj?.id);
    setIsSuccess(true);

    const completedData = {
      ...(orderPayload || {}),
      orderId: serverOrderNumber,
      dbOrderId: serverOrderId,
      paymentIntentId: piId || paymentIntentObj?.id,
      totalAmount: displayAmount,
      paymentMethod: "card",
      status: "paid",
    };

    pushGTMEvent("purchase", {
      transaction_id: piId,
      value: displayAmount,
      currency: currency || "EUR",
      items: (orderPayload?.items || [{ productName: displayProductName, price: displayAmount }]).map((it) => ({
        item_name: it.productName || it.product_name || "Krúžok",
        price: parseFloat(it.unitPrice ?? it.price ?? displayAmount),
        quantity: 1,
      })),
    });

    if (parentOnSuccess) {
      parentOnSuccess(completedData);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      className="stripe-modal"
    >
      <div className="stripe-modal__body" style={{ padding: "20px" }}>
        {isSuccess ? (
          <div
            style={{ textAlign: "center", color: "#166534", padding: "20px 0" }}
          >
            <h3 style={{ marginBottom: "10px", fontSize: "1.25rem", fontWeight: 800 }}>
              Platba prebehla úspešne!
            </h3>
            <p style={{ color: "#4E3D35", fontSize: "0.95rem" }}>
              Ďakujeme, platba bola prijatá. Na váš e-mail vám zašleme potvrdenie s faktúrou.
            </p>
            {paymentIntentId && (
              <p
                style={{
                  marginTop: "12px",
                  fontSize: "0.85rem",
                  color: "#5C4F48",
                }}
              >
                ID transakcie:{" "}
                <code
                  style={{
                    background: "#EFE9DF",
                    padding: "3px 6px",
                    borderRadius: "4px",
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                    color: "#241E1B",
                  }}
                >
                  {paymentIntentId}
                </code>
              </p>
            )}
            <Button
              variant="primary"
              onClick={onClose}
              style={{ marginTop: "20px" }}
            >
              Hotovo
            </Button>
          </div>
        ) : (
          <>
            <div
              style={{
                background: "#F5F0E8",
                border: "1px solid #E6DED2",
                padding: "16px 18px",
                borderRadius: "10px",
                marginBottom: "20px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "0.95rem", fontWeight: 600, color: "#3B2E28" }}>
                  {displayProductName}
                </span>
                <strong style={{ fontSize: "1.2rem", fontWeight: 800, color: "#241E1B" }}>
                  {displayAmount.toFixed(2)} {formatCurrency(currency)}
                </strong>
              </div>
            </div>

            {clientSecret ? (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: {
                    theme: "stripe",
                    variables: {
                      colorPrimary: "#3B2E28",
                      colorBackground: "#ffffff",
                      colorText: "#241E1B",
                      borderRadius: "3px",
                    },
                  },
                  locale: "sk",
                }}
              >
                <CheckoutForm
                  amount={displayAmount}
                  onClose={onClose}
                  onSuccess={handlePaymentSuccess}
                  currency={currency}
                />
              </Elements>
            ) : initError ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  color: initErrorType === "PROCESSING" ? "#854d0e" : "#991b1b",
                  background: initErrorType === "PROCESSING" ? "#fefce8" : "#fef2f2",
                  borderRadius: "10px",
                  border: `1px solid ${initErrorType === "PROCESSING" ? "#fef08a" : "#fecaca"}`,
                }}
              >
                <h4 style={{ marginBottom: "10px", fontWeight: 700 }}>
                  {initErrorType === "PROCESSING"
                    ? "⏳ Platba sa spracováva"
                    : "🛑 Platba bola zablokovaná"}
                </h4>
                <p style={{ fontSize: "0.9rem", lineHeight: "1.5" }}>{initError}</p>
                <Button onClick={onClose} variant="ghost" style={{ marginTop: "16px" }}>
                  Zatvoriť
                </Button>
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "30px 20px", color: "#5C4F48" }}>
                Pripravujem bezpečnú platobnú bránu Stripe...
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

export default StripePaymentModal;
